import type { Document, Node } from "./document";
import { NODE_LABELS } from "./factory";
import { plainText } from "./text";

/**
 * Layer names people can read: photos from their file name, text from its first words, and
 * never an asset / media id (Canva imports used to name photos "MAG92QKzyHc").
 */

/**
 * Does this look like a machine id rather than a name? One token of 8+ letters/digits that
 * breaks into many short case/digit runs ("MAG92QKzyHc", "HE1x6WGwieh6fXPr"), or a long hex
 * hash / UUID. File and camel-case names ("IMG_1234", "DSC01234", "iPhone12Pro", "McDonaldsLogo") are not.
 */
export function looksLikeId(s: string): boolean {
  const t = s.trim();
  if (/^[0-9a-f]{12,}$/i.test(t) && /\d/.test(t) && /[a-f]/i.test(t)) return true;
  if (/^[0-9a-f]{8}(-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i.test(t)) return true;
  if (!/^[A-Za-z0-9]{8,}$/.test(t)) return false;
  // Split like camel case: words read as a few long parts, ids as many tiny ones.
  const parts = t.match(/[A-Z]?[a-z]+|[A-Z]+(?![a-z])|\d+/g) ?? [];
  return parts.length >= 4 && t.length / parts.length < 2.6;
}

/** Layer name for a photo uploaded as `fileName`: no folders or extension; "Photo" when there's nothing readable. */
export function photoName(fileName: string | undefined | null): string {
  const base = (fileName ?? "")
    .split(/[\\/]/)
    .pop()!
    .replace(/\.[a-z0-9]{1,5}$/i, "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 60)
    .trim();
  // Pasted images arrive as "image.png" / "blob": not a name either.
  return !base || looksLikeId(base) || /^(image|img|blob|untitled|download)$/i.test(base) ? "Photo" : base;
}

/** Layer name for a text box: its first few words (up to ~32 characters), or "Text". */
export function textName(text: string): string {
  const words = text.replace(/\s+/g, " ").trim().split(" ");
  let out = "";
  for (const w of words) {
    if (out && out.length + 1 + w.length > 32) break;
    out = out ? `${out} ${w}` : w;
  }
  return out.slice(0, 40) || "Text";
}

/**
 * Is a text node's name still the automatic one for `text` (so editing the text may rename it)?
 * True for the default, for its first words, and for the older "first 40 characters" names.
 */
export function isAutoTextName(name: string, text: string): boolean {
  return name === NODE_LABELS.text || name === textName(text) || name === text.slice(0, 40) || looksLikeId(name);
}

/**
 * Load-time fix-up (no migration): nodes named after an id get a readable name. Returns the
 * same document when nothing changes.
 */
export function fixLayerNames(doc: Document): Document {
  let nodes: Record<string, Node> | null = null;
  for (const [id, n] of Object.entries(doc.nodes)) {
    const name = readableName(doc, n);
    if (name === n.name) continue;
    nodes ??= { ...doc.nodes };
    nodes[id] = { ...n, name } as Node;
  }
  return nodes ? { ...doc, nodes } : doc;
}

function readableName(doc: Document, n: Node): string {
  const assetId = "assetId" in n ? (n.assetId as string) : null;
  if (!(n.name === n.id || (assetId && n.name === assetId) || looksLikeId(n.name))) return n.name;
  if (n.type === "text") return textName(plainText(n.spans));
  if (n.type === "image") return photoName(assetId ? doc.assets[assetId]?.name : null);
  return NODE_LABELS[n.type];
}
